// src/views/StoreAnalytics.js
import React, { useState, useEffect } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CWidgetStatsA,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CFormSelect,
  CSpinner,
  CFormLabel,
} from "@coreui/react";
import { CChartBar, CChartDoughnut, CChartLine } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import { cibEventStore, cilChartPie, cilMap, cilCalendar } from "@coreui/icons";
import { getSummary, getGeographicDistribution, getSalesPerformance, getMapDistribution, getVisibilityTrends, getStockPerStoreMonthly } from "src/services/Analytics";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import SalesPicker from "src/components/SalesPicker";
import ls from "local-storage";
import StockLatest from "src/components/StockLatest";

// Fix for default marker icons (required for React-Leaflet)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});
const StoreAnalytics = () => {
  const [summary, setSummary] = useState([]);
  // const [visitTrends, setVisitTrends] = useState([]);
  const [visibilityTrends, setVisibilityTrends] = useState([]);
  const [mapDistribution, setMapDistribution] = useState([]);
  const [geoDistribution, setGeoDistribution] = useState([]);
  const [salesPerformance, setSalesPerformance] = useState([]);
  const [timeRange, setTimeRange] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [stockMonthly, setStockMonthly] = useState(null);
  const navigate = useNavigate();

  const center = [-6.91, 107.61]; // Latitude, Longitude of Bandung

  const getFilterParams = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");
    
    return {
      // dateFrom: Moment(dateRange[0].startDate).format("YYYY-MM-DD"),
      // dateTo: Moment(dateRange[0].endDate).format("YYYY-MM-DD"),
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  };

  const fetchData = React.useCallback(async () => {
    const filters = getFilterParams();
    setLoading(true);
    try {
      const [resx, maps, geo, sales] = await Promise.all([
        getSummary( filters ),
        getMapDistribution( filters ),
        // getVisitTrends({ timeRange }),
        getGeographicDistribution(),
        getSalesPerformance(),
      ]);
      setSummary(resx);
      setMapDistribution(maps.stores);
      // setVisitTrends(trends);
      setGeoDistribution(geo.data);
      setSalesPerformance(sales.data);
      // visibility trend fetch should not block page
      try {
        const vis = await getVisibilityTrends(timeRange, filters);
        setVisibilityTrends(vis?.data || vis || []);
      } catch (e) {
        setVisibilityTrends([]);
      }
      // stock per store monthly should not block page
      try {
        const monthly = await getStockPerStoreMonthly({ ...filters, months: 6 });
        setStockMonthly(monthly?.data || monthly || null);
      } catch (e) {
        setStockMonthly([]);
      }
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const buildVisibilityTrendDataset = (raw) => {
    if (!raw) return { labels: [], datasets: [] };
    // Shape 1: { data: { labels:[], series:[{label, data}] } }
    const maybe = raw?.labels && raw?.series ? raw : raw?.data?.labels ? raw.data : null;
    if (maybe) {
      return {
        labels: maybe.labels,
        datasets: (maybe.series || []).map((s, idx) => ({
          label: s.label || s.name || `Series ${idx + 1}`,
          data: s.data || s.values || [],
          borderColor: ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384"][idx % 4],
          backgroundColor: "transparent",
          tension: 0.3,
        })),
      };
    }
    // Shape 2A (long form): Array of objects [{ month, visibility, visit_count }]
    // Pivot into datasets per visibility across months
    const arr = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
    if (!arr.length) return { labels: [], datasets: [] };
    const isLongForm = Object.prototype.hasOwnProperty.call(arr[0], "month") &&
      (Object.prototype.hasOwnProperty.call(arr[0], "visibility") || Object.prototype.hasOwnProperty.call(arr[0], "category")) &&
      (Object.prototype.hasOwnProperty.call(arr[0], "visit_count") || Object.prototype.hasOwnProperty.call(arr[0], "count"));
    if (isLongForm) {
      const months = [...new Set(arr.map((r) => r.month))];
      const categories = [...new Set(arr.map((r) => r.visibility || r.category))];
      const palette = ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384", "#9966FF", "#FF9F40"];
      const datasets = categories.map((cat, idx) => ({
        label: cat,
        data: months.map((m) => {
          const found = arr.find((r) => r.month === m && (r.visibility || r.category) === cat);
          return Number((found && (found.visit_count ?? found.count)) ?? 0);
        }),
        borderColor: palette[idx % palette.length],
        backgroundColor: "transparent",
        tension: 0.3,
      }));
      return { labels: months, datasets };
    }
    // Shape 2B (wide form): Array of objects [{ period/date/month, Good, Fair, Poor }]
    const labels = arr.map((r) => r.month || r.period || r.date || "");
    const categoryKeys = Object.keys(arr[0]).filter((k) => !["month", "period", "date"].includes(k));
    const datasets = categoryKeys.map((key, idx) => ({
      label: key,
      data: arr.map((r) => Number(r[key] ?? 0)),
      borderColor: ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384", "#9966FF"][idx % 5],
      backgroundColor: "transparent",
      tension: 0.3,
    }));
    return { labels, datasets };
  };

  const buildMonthlyChart = (raw) => {
    if (!raw) return { labels: [], datasets: [] };
    const obj = raw?.labels && raw?.series
      ? raw
      : (raw?.months && raw?.stores)
        ? { labels: raw.months, series: raw.stores.map(s => ({ label: s.store_name || s.store || s.name, data: s.values || s.data || [] })) }
        : null;
    if (obj) {
      return {
        labels: obj.labels,
        datasets: (obj.series || []).map((s, idx) => ({
          label: s.label,
          data: s.data,
          borderColor: ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384", "#9966FF", "#FF9F40"][idx % 6],
          backgroundColor: "transparent",
          tension: 0.3,
        })),
      };
    }
    const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    if (!arr.length) return { labels: [], datasets: [] };
    const months = [...new Set(arr.map((r) => r.month || r.period || r.date))];
    const storeNames = [...new Set(arr.map((r) => r.store_name || r.store || r.name))];
    const datasets = storeNames.map((store, idx) => ({
      label: store,
      data: months.map((m) => {
        const found = arr.find((r) => (r.month || r.period || r.date) === m && (r.store_name || r.store || r.name) === store);
        return Number(found?.total_stock ?? found?.total ?? found?.stock ?? found?.value ?? 0);
      }),
      borderColor: ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384", "#9966FF", "#FF9F40"][idx % 6],
      backgroundColor: "transparent",
      tension: 0.3,
    }));
    return { labels: months, datasets };
  };
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  return (
    <div className="store-analytics">
      <SalesPicker/>
      <StockLatest/>
      <CRow>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="primary"
            value={summary.data.summary.totalStores}
            title="Total Toko"
            icon={<CIcon icon={cibEventStore} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="info"
            value={summary.data.distribution.filter((s) => s.type === "A").map((s) => s.count) || 0}
            title="Toko Tipe A"
            icon={<CIcon icon={cilCalendar} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="warning"
            value={summary.data.summary.visitedStores}
            title="Toko Terkunjungi"
            icon={<CIcon icon={cilMap} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="danger"
            value={summary.data.summary.notVisitedStores}
            title="Toko Belum Terkunjungi"
            icon={<CIcon icon={cilChartPie} height={36} />}
          />
        </CCol>
      </CRow>
      <CRow>
        {/* Stock per Store (Monthly) */}
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow className="align-items-center">
                <CCol>Stock per Store (Monthly)</CCol>
                <CCol className="text-end">
                  <small className="text-muted">Last 6 months</small>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              {(() => {
                const chart = buildMonthlyChart(stockMonthly);
                return chart.labels.length ? (
                  <CChartLine
                    data={{ labels: chart.labels, datasets: chart.datasets }}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: "bottom" } },
                      interaction: { mode: "index", intersect: false },
                      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                    }}
                    style={{ minHeight: 300 }}
                  />
                ) : (
                  <div className="text-muted">Tidak ada data stok bulanan</div>
                );
              })()}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        {/* Visibility Trend */}
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow className="align-items-center">
                <CCol>Tren Visibility</CCol>
                <CCol className="text-end">
                  <CFormSelect
                    size="sm"
                    style={{ width: "auto", display: "inline-block" }}
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value)}
                  >
                    <option value="weekly">7 Hari</option>
                    <option value="biweekly">14 Hari</option>
                    <option value="monthly">30 Hari</option>
                    <option value="quarterly">90 Hari</option>
                  </CFormSelect>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              {(() => {
                const chart = buildVisibilityTrendDataset(visibilityTrends);
                return chart.labels.length ? (
                  <CChartLine
                    data={{ labels: chart.labels, datasets: chart.datasets }}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: "bottom" } },
                      interaction: { mode: "index", intersect: false },
                      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                    }}
                    style={{ minHeight: 260 }}
                  />
                ) : (
                  <div className="text-muted">Tidak ada data tren</div>
                );
              })()}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow>
                <CCol>Distribusi toko berdasarkan tipe</CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: summary.data.distribution.map((item) => `Type ${item.type}`),
                  datasets: [
                    {
                      backgroundColor: ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384"],
                      data: summary.data.distribution.map((item) => item.count),
                    },
                  ],
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow>
                <CCol>Distribusi toko berdasarkan visibility</CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: summary.data.visibility.map((item) => ` ${item.visibility}`),
                  datasets: [
                    {
                      backgroundColor: ["#4BC0C0", "#36A2EB", "#FFCE56"],
                      data: summary.data.visibility.map((item) => item.store_count),
                    },
                  ],
                }}
              />
              <CFormLabel>*Berdasarkan data kunjungan terakhir</CFormLabel>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>Sebaran Toko Berdasarkan Lokasi</CCardHeader>
            <CCardBody>
              <MapContainer center={center} zoom={13} style={{ height: "600px", width: "100%" }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {mapDistribution.map((store) => (
                  <Marker key={store.id} position={isNaN(store.latitude) ? center : [store.latitude, store.longitude]}>
                    <Popup>
                      <div style={{ cursor: "pointer" }} onClick={() => navigate(`/stores/details/${store.id}`)}>
                        <h3>{store.name}</h3>
                        <p>{store.address}</p>
                        <p style={{ color: "#0066cc", textDecoration: "underline", marginTop: "8px" }}>
                          Lihat Detail →
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>Geographic Distribution</CCardHeader>
            <CCardBody>
              <CChartBar
                data={{
                  labels: geoDistribution.map((item) => item.province),
                  datasets: [
                    {
                      label: "Stores per Province",
                      backgroundColor: "#f87979",
                      data: geoDistribution.map((item) => item.store_count),
                    },
                  ],
                }}
                options={{
                  indexAxis: "y",
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "right",
                    },
                  },
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard>
            <CCardHeader>Stores Needing Attention</CCardHeader>
            <CCardBody style={{ maxHeight: "400px", overflowY: "auto" }}>
              <CTable striped hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Store</CTableHeaderCell>
                    <CTableHeaderCell>Type</CTableHeaderCell>
                    <CTableHeaderCell>Last Visit</CTableHeaderCell>
                    <CTableHeaderCell>Days Since Visit</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {salesPerformance.map((store) => (
                    <CTableRow key={store.id}>
                      <CTableDataCell>{store.store_name}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={store.type === "A" ? "primary" : "secondary"}>Type {store.type}</CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        {store.last_visited ? new Date(store.last_visited).toLocaleDateString() : "Never"}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={store.days_since_last_visit > 30 ? "danger" : "warning"}>
                          {store.days_since_last_visit || "N/A"}
                        </CBadge>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  );
};

export default StoreAnalytics;
